/**
 * Setup-revision status machine. A revision starts when the user returns from
 * the mapping workspace to a mutable setup stage and only becomes `ready`
 * again after the Revit mutation, the persisted-and-read-back profile and
 * mapping readiness all agree on the same document and setup fingerprint.
 */

export const SetupRevisionStatuses = [
  'ready',
  'revisionOpen',
  'dirty',
  'reconciling',
  'profileUnverified',
  'readinessChecking',
  'recoveryRequired',
] as const;

export type SetupRevisionStatus = (typeof SetupRevisionStatuses)[number];

export type SetupRevisionEvent =
  | 'returnToSetup'
  | 'valueChanged'
  | 'reconfirmUnchanged'
  | 'saveAndContinue'
  | 'discardRestored'
  | 'revitTargetVerified'
  | 'revitOperationFailed'
  | 'recoveryRequiredAfterUndo'
  | 'profileReadBackVerified'
  | 'profileVerificationFailed'
  | 'readinessVerified'
  | 'readinessFailed'
  | 'recoveryRetryAllowed';

export const InitialSetupRevisionStatus: SetupRevisionStatus = 'ready';

/**
 * The only accepted transitions. Anything absent here is rejected rather than
 * silently ignored, so a caller can never nudge a revision into `ready`
 * through an event the contract does not allow.
 */
const Transitions: Record<SetupRevisionStatus, Partial<Record<SetupRevisionEvent, SetupRevisionStatus>>> = {
  ready: {
    returnToSetup: 'revisionOpen',
  },
  revisionOpen: {
    valueChanged: 'dirty',
    reconfirmUnchanged: 'reconciling',
    returnToSetup: 'revisionOpen',
  },
  dirty: {
    valueChanged: 'dirty',
    saveAndContinue: 'reconciling',
    discardRestored: 'revisionOpen',
  },
  reconciling: {
    revitTargetVerified: 'profileUnverified',
    revitOperationFailed: 'dirty',
    recoveryRequiredAfterUndo: 'recoveryRequired',
  },
  profileUnverified: {
    profileReadBackVerified: 'readinessChecking',
    profileVerificationFailed: 'profileUnverified',
  },
  readinessChecking: {
    readinessVerified: 'ready',
    readinessFailed: 'profileUnverified',
  },
  recoveryRequired: {
    recoveryRetryAllowed: 'reconciling',
  },
};

/** Returns the next status, or `null` when the transition is not allowed. */
export const nextSetupRevisionStatus = (
  status: SetupRevisionStatus,
  event: SetupRevisionEvent
): SetupRevisionStatus | null => Transitions[status]?.[event] ?? null;

/**
 * Whether setup may be reopened for `projectKey`. Another project always gets
 * its own revision; the project that already owns one may only reopen it while
 * a repeated return to setup is still a legal move, so an unsettled revision is
 * never replaced by a fresh one that would hide the work it still owes.
 */
export const canOpenSetupRevision = (
  status: SetupRevisionStatus,
  revisionProjectKey: string | null,
  projectKey: string
): boolean =>
  revisionProjectKey !== projectKey || nextSetupRevisionStatus(status, 'returnToSetup') !== null;

/** A revision is settled only when every checkpoint has been verified. */
export const isSetupRevisionSettled = (status: SetupRevisionStatus): boolean => status === 'ready';

/**
 * Whether a verified read-back may be recorded as the new baseline. A revision
 * waiting on its profile records the evidence and moves on to the readiness
 * check; a session with no revision in flight is already settled and merely
 * records it. Every other status describes setup that is still being changed,
 * so its evidence is not final and is refused.
 */
export const canRecordVerifiedSetup = (status: SetupRevisionStatus): boolean =>
  isSetupRevisionSettled(status) ||
  nextSetupRevisionStatus(status, 'profileReadBackVerified') !== null;

/** Phases where a checkpoint operation is running or its result is still awaited. */
export const isSetupRevisionInFlight = (status: SetupRevisionStatus): boolean =>
  status === 'reconciling' || status === 'profileUnverified' || status === 'readinessChecking';

const isSetupRevisionStatus = (value: unknown): value is SetupRevisionStatus =>
  typeof value === 'string' && (SetupRevisionStatuses as readonly string[]).includes(value);

/**
 * Statuses that describe work owned by a process that did not survive the
 * reload. Rehydration must resume at the last state the user can act on —
 * never at `ready`, which would assert a verification that never completed.
 */
const HydrationFallback: Partial<Record<SetupRevisionStatus, SetupRevisionStatus>> = {
  reconciling: 'dirty',
  readinessChecking: 'profileUnverified',
};

export const sanitizeSetupRevisionStatus = (value: unknown): SetupRevisionStatus => {
  if (!isSetupRevisionStatus(value)) {
    return InitialSetupRevisionStatus;
  }

  return HydrationFallback[value] ?? value;
};
